import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpParams} from "@angular/common/http";
import {ContentsHelper} from "../../helpers/contents.helper";
import {DetailConfigurationField} from "../../models/detail-configuration-field";
import {EMPTY, forkJoin, Observable, of, Subscription} from "rxjs";
import {catchError, finalize, map, mergeMap} from "rxjs/operators";
import {DetailConfiguration} from "../../models/detail-configuration";
import {TranslateService} from "@ngx-translate/core";
import {SharedDataService} from "../shared/shared-data.service";
import {HelperService} from "../helperService";
import {Router} from "@angular/router";
import * as Raven from "raven-js";
import {Publicationobject} from "../../models/publicationobject.model";

@Injectable()
export class DetailService {

	/**
	 * Attributes
	 */
	private ARR_subscriptions: Subscription[] = [Subscription.EMPTY];
	public OBJ_detail_content: ContentsHelper;
	private _STR_detailurl: string;
	private _classname: string;
	private OBJ_detail_configuration: DetailConfiguration;
	private ARR_urls_to_reload = [];
	private ARR_values = [];
	private ARR_contents_helper: ContentsHelper[] = [];
	private ARR_former_composition: Array<Publicationobject> = new Array<Publicationobject>();
	BOO_new_publicationobjects: boolean = false;

	/**
	 * Function > constructor
	 * @param OBJ_router
	 * @param {HttpClient} OBJ_http
	 * @param {TranslateService} OBJ_translate_service
	 * @param {SharedDataService} OBJ_subscription_service
	 * @param {HelperService} OBJ_helper_service
	 */
	constructor
	(
		private OBJ_router: Router,
		private OBJ_http: HttpClient,
		private OBJ_translate_service: TranslateService,
		private OBJ_subscription_service: SharedDataService,
		private OBJ_helper_service: HelperService
	) {
	} // end function constructor

	/**
	 * Function > clearForm
	 *
	 * @desc Sorgt dafuer dass beim Verlassen einer Komponente die entsprechenden Subscruptions zerstoert werden
	 */
	clearForm() {

		// Unsubscribe from all subscriptions to prevent memory leak
		for (let OBJ_subscription of this.ARR_subscriptions) {

			// console log
			this.OBJ_helper_service.log
			(
				{
					class: 'DetailService',
					function: 'clearForm'
				},
				"Unsubscribing from Subscription",
				OBJ_subscription
			);

			OBJ_subscription.unsubscribe();

		} // end for

		this.ARR_former_composition = new Array<Publicationobject>();
		this.BOO_new_publicationobjects = false;

	} // end function clearForm

	/**
	 * Function getAssinmentsFromConfigurationByType
	 *
	 * @param {string} type
	 * @returns {*}
	 * @memberof DetailService
	 * @description Gibt die Bilder anhand des übergebenen Typs zurück
	 */
	getAssignmentsFromConfigurationByType(type: string): any {
		let assignments = this.OBJ_detail_configuration.fields.filter(as => as.name === "assignments");
		if (assignments !== null && assignments !== undefined && assignments.length > 0) {
			return assignments[0].fields.filter(b => b.name.includes(type));
		}
		return null;

	}

	/**
	 *
	 *
	 * @param {string} source
	 * @returns {*}
	 * @memberof DetailService
	 */
	getSourceAndCaptionBySource(source: string): any {
		let sourceSplit = source.split('://')[1];
		if (sourceSplit !== undefined) {
			let assignments = this.OBJ_detail_configuration.fields.filter(as => as.name === "assignments");
			if (assignments !== null && assignments !== undefined && assignments.length > 0) {
				let assig = assignments[0].fields.filter(b => b.value === sourceSplit);
				if (assig && assig[0].object_structure && assig[0].object_structure.loaded_assignments) {

					return assig[0].object_structure.loaded_assignments[0]
				}
			}
		}

		return null;
	}

	/**
	 * Function > getFormFields
	 *
	 * @param {string} STR_config_url
	 * @param STR_data_url
	 * @param OBJ_http_content_params
	 */
	getFormFields(STR_config_url: string, STR_data_url: string, OBJ_http_content_params = null): Observable<DetailConfiguration> {

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'getFormFields'
			},
			"Loading Config from url: " + STR_config_url
		);

		// URL fuer Detailcontent ist nicht gesetzt --> Gib leeres Observable zurueck

		if (!STR_data_url) {

			this.OBJ_helper_service.log
			(
				{
					class: 'DetailService',
					function: 'getFormFields'
				},
				"Die URL zur REST-Schnittstelle ist nicht gesetzt"
			);

			return EMPTY;

		} // end if

		// Fuehre Konfiguration und Inhalt zusammen
		return forkJoin([

				this.OBJ_http.get(STR_config_url),
				this.getHelper(STR_data_url, OBJ_http_content_params, 'getFormFields')
			]
		).pipe(
			map(
				(OBJ_data: any[]) => {
					this.OBJ_detail_configuration = OBJ_data[0];
					let OBJ_contents_helper: ContentsHelper = OBJ_data[1];

					// Setze den ContentsHelper zum updaten/createn der Daten
					this.OBJ_detail_content = OBJ_contents_helper;

					// Fuege allgemeine Felder wie modiuser, modidate... hinzu
					DetailService.addGeneralFields(this.OBJ_detail_configuration.fields, OBJ_contents_helper);

					// Befuelle die Konfig mit Werten, sammle Liste aller Links, die noch nachgeladen werden muessen
					let ARR_observables = this.fillFormWithValues(this.OBJ_detail_configuration.fields, OBJ_contents_helper, []);

					this.OBJ_helper_service.logWithClassAndFunction("DetailService", "getFormFields", ARR_observables);

					// Fuege Die Detailconfig als "kuenstliches" Observable dazu, um alles gesammelt an mergeMap und forkJoin weitergeben zu koennen
					ARR_observables.unshift(of(this.OBJ_detail_configuration));

					return ARR_observables;
				}), // end map
			mergeMap(
				(ARR_observables: Array<Observable<any>>) => {
					// ForkJoin subscribed sich an alle Observables, wartet bis sie beendet sind und gibt von allen letzten Wert in Array zurueck
					// Da die Http.get-Funktion genau den REST-Call ausfuehrt und dann completed erhalten wir alle Daten die wir benoetigen
					return forkJoin(ARR_observables).pipe(
						map((OBJ_data: any[]) => {

							// Detailconfig ist in erstem Eintrag des arrays - forkJoin hat den Wert aus dem "kuenstlichen" Observable herausgeholt
							let OBJ_detail_config: DetailConfiguration = OBJ_data.shift();

							// Die restlichen Eintraege sind nachgeladene Daten der REST-Schnittstelle, weshalb wir hier den Contentshelper verwenden koennen
							this.ARR_contents_helper = OBJ_data;

							this.OBJ_helper_service.logWithClassAndFunction("DetailService", "getFormFields", "Nachgeladene Daten: ", this.ARR_contents_helper);

							this.appendReloadedData(OBJ_detail_config.fields, this.ARR_contents_helper);

							return OBJ_detail_config;

						})); // end map

				}), // end mergeMap
			finalize(() => {
				// When finisehd clear this array
				this.ARR_urls_to_reload = [];
			}) // end finalize
		); // end pipe

	} // end function getFormFields

	/**
	 * Function > getFormConfig
	 *
	 * @desc Funktion die NUR die Config-Dateein zurueckliefert, kein Matching mit irgendwelchen Backend-Daten
	 *
	 * @param STR_config_url
	 */
	getFormConfig(STR_config_url: string): Observable<any> {
		return this.OBJ_http.get(STR_config_url);
	} // end function getFormConfig

	/**
	 * Function > appendReloadedData
	 *
	 * @desc Fuehre nachgeladene Daten wie Dropdown-Optionen... mit dem Formular zusammen
	 * @param ARR_form_fields
	 * @param ARR_contents_helper
	 */
	appendReloadedData(ARR_form_fields: Array<DetailConfigurationField>, ARR_contents_helper: Array<ContentsHelper>) {

		for (let OBJ_field of ARR_form_fields) {

			// Aufbau eines Elementes nachgeladen
			if (DetailService.externalComposition(OBJ_field)) {

				for (let OBJ_helper of ARR_contents_helper) {

					// besitzt Feld Aufbau, der extern nachgeladen wurde?
					if (
						(
							OBJ_helper.url == OBJ_field._links.composition.href ||
							OBJ_helper.url == OBJ_field._links.composition.href_temp
						) &&
						(OBJ_field.type == "array" || OBJ_field.type == "publicationobjects")
					) {
						// Hole Aufbau des arrays
						let ARR_array_value: Publicationobject[] = OBJ_helper.getValue(OBJ_field.name);

						// Hole alten Aufbau des arrays
						let ARR_old_structure = this.ARR_former_composition[OBJ_field.name];

						let counter = 0;
						let supplemented_array_values: Publicationobject[] = [];
						// Lege Array mit Publicationobjects an und befuelle sie mit Werten, sofern vorhanden
						ARR_array_value.forEach(
							publicationobject => {

								// Hole erstes noch nicht zugeordnetes Publicationobject der alten Struktur
								let OBJ_old_publicationobject = ARR_old_structure[0];

								// Setze Wert des alten Publicationobjectes fuer neue Struktur - aber nur wenn type uebereinstimmt
								if (OBJ_old_publicationobject && OBJ_old_publicationobject.type === publicationobject.type) {
									publicationobject.new = false;
									publicationobject.id = OBJ_old_publicationobject.id;
									publicationobject.ids = OBJ_old_publicationobject.ids;
									publicationobject.datefrom = OBJ_old_publicationobject.datefrom;
									publicationobject.dateto = OBJ_old_publicationobject.dateto;
									publicationobject.entity = OBJ_old_publicationobject.entity;
									// Wert konnte zugeordnet werden, entferne das Objekt aus der Liste der alten POs
									ARR_old_structure.shift();
								} else {
									// Veraendertes Publicationobject ist hinzugekommen, markieren um Nutzer Bescheid zu geben
									publicationobject.new = true;
									this.BOO_new_publicationobjects = true;
									OBJ_field.new = true;
								} // end if

								supplemented_array_values.push(publicationobject);
								counter++;
							}
						); // end foreach

						// Erstelle Felder des Arrays
						this.createArrayFields(supplemented_array_values, OBJ_field);

					} // end if

				} // end for

			} // end if

			// Optionen nachgeladen
			if (DetailService.externalOptions(OBJ_field)) {
				this.fillExternalOptions(OBJ_field, ARR_contents_helper);
			} // end if

			// Befuelle innere Felder in arrays & objekten
			if (
				OBJ_field.type == "array" ||
				OBJ_field.type == "publicationobjects" ||
				OBJ_field.type == "array_static" ||
				OBJ_field.type == "assignments" ||
				OBJ_field.type == "object"
			) {

				for (let OBJ_inner_field of OBJ_field.fields) {

					if (OBJ_inner_field.type == "object") {

						for (let OBJ_object_field of OBJ_inner_field.fields) {

							if (DetailService.externalOptions(OBJ_object_field)) {
								this.fillExternalOptions(OBJ_object_field, ARR_contents_helper);
							} // end if

						} // end for

					} else if (DetailService.externalOptions(OBJ_inner_field)) {
						this.fillExternalOptions(OBJ_inner_field, ARR_contents_helper);
					} // end if

				} // end for

			} // end if

		} // end for

		// Reset the urls, next time reload all of them
		this.ARR_urls_to_reload = [];

	} // end function appendReloadedData

	/**
	 * Function > fillFormWithValues
	 *
	 * @param ARR_form_fields: FieldConfig[]
	 * @param OBJ_contents_helper
	 * @param ARR_observables
	 * @param {string} STR_object_name
	 * @param BOO_nested_values
	 */
	fillFormWithValues(
		ARR_form_fields: DetailConfigurationField[],
		OBJ_contents_helper: ContentsHelper,
		ARR_observables = [],
		STR_object_name = '',
		BOO_nested_values = false,
		readonly: boolean = false
	): Array<Observable<any>> {

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'fillFormWithValues'
			},
			"Filling form with values, these are my form fields",
			ARR_form_fields
		);

		// Liste an Feldern, von denen andere Felder abhaengen
		let ARR_dependencies = [];

		// Falls es sich um inneres Objekt handelt, baue Pfad dorthin (i.e. Objectname.innerObjectname.innermostObjectname)
		let STR_path_to_value = "";
		if (BOO_nested_values && STR_object_name.length > 0) {
			STR_path_to_value = STR_object_name + ".";
		} // end if

		// Iteriere ueber alle ueber die Konfig mitgegebenen Felder und fuelle sie mit den Werten der REST-Schnittstelle
		for (let OBJ_config_entry of ARR_form_fields) {

			// nested objects
			if (typeof OBJ_config_entry.fields !== 'undefined') {

				// find dependencies between fields
				for (let OBJ_field of OBJ_config_entry.fields) {
					this.findDependencies(OBJ_field, ARR_dependencies);
				} // end for

			} // end if

			this.findDependencies(OBJ_config_entry, ARR_dependencies);
			this.enhanceField(OBJ_config_entry, ARR_observables, OBJ_contents_helper, [], STR_path_to_value, readonly);

		} // end for

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'fillFormWithValues'
			},
			"Das ist mein array mit abhaengigkeiten",
			ARR_dependencies
		);

		// Verknuepfe abhaengioe Felder miteinander
		this.createParentFieldObservers(ARR_dependencies, ARR_form_fields);

		return ARR_observables;

	} // end function fillFormWithValues

	/**
	 * @desc Find dependencies
	 * @param {DetailConfigurationField} OBJ_field
	 * @param {array} ARR_dependencies
	 */
	private findDependencies(OBJ_field, ARR_dependencies): void {

		if (OBJ_field.dependent_fields) {

			let STR_name = OBJ_field.name;
			// Sammle alle Felder die von einander abhaengen
			ARR_dependencies.push({[STR_name]: OBJ_field.dependent_fields});

			// console log
			this.OBJ_helper_service.log
			(
				{
					class: 'DetailService',
					function: 'fillFormWithValues'
				},
				"Dependencies found: ",
				ARR_dependencies
			);

		} // end if

	} // end function findDependencies

	/**
	 * Function > createArrayFields
	 *
	 * @desc Erstelle die Felder eines arrays - ! Aktuell nur für select_multiple und input implementiert
	 * @param array_values
	 * @param field
	 */
	public createArrayFields(array_values, field: DetailConfigurationField) {

		field.fields = [];
		let counter = 1;

		// Ueber Felder des array iterieren und anpassen
		for (const value of array_values) {

			// Hole Konfig fuer Elemente mit diesem Namen
			const OBJ_arrayfield_config: DetailConfigurationField = field.fieldconfig.find(OBJ_config_entry => OBJ_config_entry.name === value.type);

			// Ist Config von diesem Feld vorhanden? Wenn nicht, wird es ignoriert
			if (OBJ_arrayfield_config) {

				// Hole Config des Feldes, passe Name an, damit es nicht zu Dopplungen kommt
				let OBJ_detail_field: DetailConfigurationField = Object.assign({}, OBJ_arrayfield_config, {"name": (OBJ_arrayfield_config.name + "_" + counter)});

				// Speichere den Aufbau des Objectes zwischen um es spaeter wieder korrekt ans backend schicken zu können
				OBJ_detail_field.object_structure = value;

				// Markiere neu hinzugekommenen Felder
				OBJ_detail_field.new = value.new;

				// Je nach Art des Feldes muessen Werte anders befuellt werden
				switch (OBJ_detail_field.type) {
					case "select_multiple":
						if (OBJ_detail_field.multiple) {
							if (value.ids === null || value.ids === undefined) {
								// Ist das Feld noch nicht befuellt wird ein leerer Eintrag fuer value erzeugt
								OBJ_detail_field.value = [];
							} else {
								// Name wird spaeter aus den optionen befuellt, hier ist nur ID bekannt
								OBJ_detail_field.value = value.ids;
							} // end if
						} else {
							if (value.id === null || value.id === undefined) {
								// Ist das Feld noch nicht befuellt wird ein leerer Eintrag fuer value erzeugt
								OBJ_detail_field.value = [];
							} else {
								// Name wird spaeter aus den optionen befuellt, hier ist nur ID bekannt
								OBJ_detail_field.value = [value.id];
							} // end if
						} // end if

						break;

					case "input":
						OBJ_detail_field.value = value.id;
						break;

					case "date":
						if(OBJ_detail_field.selectMode == "range")
						{
							OBJ_detail_field.value = [value.datefrom, value.dateto];
						}
						else
						{
							OBJ_detail_field.value = value.datefrom;
						}
						break;

				} // end if

				field.fields.push(
					OBJ_detail_field
				);


			}
			// Besonderer Typ, der erlaubt, Inhalt, Typ und Ausspieldatum des Inhaltes festzulegen
			else if(value.type === "itemdateselect")
			{
				// Von welcher Art ist der Inhalt
				let detail_field_type: DetailConfigurationField = {"name": "select_" + counter, "type": "select"};
				detail_field_type.object_structure = Object.assign({}, value);
				// Markiere neu hinzugekommenen Felder
				detail_field_type.new = value.new;
				// Wert wird in Feld entity gespeichert
				detail_field_type.value = (value.entity !== null)?value.entity:"";
				detail_field_type.options = [
					{
						"id": "webinar",
						"name": "webinar"
					},
					{
						"id": "whitepaper",
						"name": "whitepaper"
					},
					{
						"id": "advertorial",
						"name": "advertorial"
					}
				];
				field.fields.push(
					detail_field_type
				);

				// ID des Inhaltes
				let detail_field_input: DetailConfigurationField = {"name": "item_" + counter, "type": "input"};
				detail_field_input.object_structure = Object.assign({}, value);
				// Markiere neu hinzugekommenen Felder
				detail_field_input.new = value.new;
				detail_field_input.value = value.id;
				detail_field_input.object_structure.title = "ID";
				field.fields.push(
					detail_field_input
				);

				// Zeitraum, in dem Inhalt ausgespielt wird
				let detail_field_date: DetailConfigurationField = {"name": "date_" + counter, "type": "date"};
				detail_field_date.object_structure = Object.assign({}, value);
				// Markiere neu hinzugekommenen Felder
				detail_field_date.new = value.new;
				detail_field_date.object_structure.title = "daterange";
				// daterange, kein Einzeldatum
				detail_field_date.value = [value.datefrom, value.dateto];
				detail_field_date.datepicker_type = "calendar";
				detail_field_date.selectMode = "range";
				field.fields.push(
					detail_field_date
				);

				// Trennlinie hinzufuegen
				field.fields.push({
					"name": "separation line",
					"type": "line",
				});

			} // end if

			counter++;

		} // end for

	} // end function createArrayFields

	/**
	 * Function > fillOptionsSelect
	 *
	 * @desc Befuelle Dropdown mit uebergebenen Werten
	 *
	 * @param {DetailConfigurationField} OBJ_config_entry
	 * @param OBJ_helper

	 */
	public fillOptionsSelect(OBJ_config_entry: DetailConfigurationField, OBJ_helper: ContentsHelper) {

		OBJ_config_entry.options = [];

		if (OBJ_config_entry.additional_options) {

			// Klone die Elemente des Array, damit sie sich nicht bei jedem Nachladen doppeln
			OBJ_config_entry.options = OBJ_config_entry.additional_options.map(obj => ({...obj}));
		} // end if

		// Optionen sind in Liste mit hinterlegtem Namen
		let ARR_options_objects = OBJ_helper.getListingData();



		// Optionen hinzufuegen
		ARR_options_objects.forEach(OBJ_option => {
			OBJ_config_entry.options.push(this.getOption(OBJ_option, OBJ_config_entry));
		});

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'fillOptionsSelect'
			},
			"Select filled, this is what I got",
			ARR_options_objects
		);

	} // end function fillOptionsSelect

	getOption(OBJ_option, OBJ_config) {
		let STR_id = OBJ_option.id;
		let STR_name = OBJ_option.title;

		if (OBJ_config._links.options.option) {
			if (typeof OBJ_option[OBJ_config._links.options.option.id] !== 'undefined') {
				STR_id = OBJ_option[OBJ_config._links.options.option.id];
			}
			if (typeof OBJ_option[OBJ_config._links.options.option.title] !== 'undefined') {
				STR_name = OBJ_option[OBJ_config._links.options.option.title];
			}
		}

		return {
			"id": STR_id,
			"name": STR_name,
			"deprecated": OBJ_option.deprecated
		};
	}

	/**
	 * Function > fillOptionsSelectMultiple
	 *
	 * @desc Befuelle Dropdown mit Mehrfachauswahl mit nachgeladenen Werten
	 * @param {DetailConfigurationField} OBJ_config_entry
	 * @param OBJ_helper
	 * @param STR_options_name
	 */
	fillOptionsSelectMultiple(OBJ_config_entry: DetailConfigurationField, OBJ_helper: ContentsHelper, STR_options_name: string) {

		const shouldSort = OBJ_config_entry.sort !== false;

		// wenn in der Config keine default-Optionen hinterlegt sind, lege leeres Array an
		OBJ_config_entry.options = OBJ_config_entry.options || [];

		// Optionen sind in Liste mit hinterlegtem Namen
		let ARR_options_objects = OBJ_helper.getListingData();

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'fillOptionsSelectMultiple'
			},
			OBJ_config_entry,  ARR_options_objects,OBJ_helper
		);


		// Ist Wert nur mit ID befuellt muss er fuer Anzeige erweitert wernden
		let ARR_values = [];
		let BOO_enhance_value = false;
		if (OBJ_config_entry.value !== null && OBJ_config_entry.value !== undefined && OBJ_config_entry.value.length > 0 && typeof OBJ_config_entry.value[0] !== 'object') {
			BOO_enhance_value = true;
		} // end if

		// Optional: Mapping für die spätere Reihung ohne Sortierung
		const mapById: { [id: string]: { id: string | number; name: string } } = {};

		// Optionen hinzufuegen
		for (let OBJ_option_data of ARR_options_objects) {

			let STR_display_value = "";

			// Ist konfiguriert, wie Optionen beschriftet werden sollen?
			if (OBJ_config_entry._links.options.displayfields) {

				for (let STR_fieldname of OBJ_config_entry._links.options.displayfields) {
					STR_display_value += OBJ_option_data[STR_fieldname] + " ";
				} // end for

				// Entferne ueberfluessiges Leerzeichen
				STR_display_value = STR_display_value.substr(0, STR_display_value.length - 1);

			} else {
				STR_display_value = OBJ_option_data.title;
			} // end if

			const entry = {
				id: OBJ_option_data.id,
				name: STR_display_value
			};

			// Optionen befuellen
			OBJ_config_entry.options.push(entry);

			if (!shouldSort) {
				mapById[OBJ_option_data.id] = entry;
			}

			// Um Feld korrekt anzuzeigen setzte das Textfeld
			if (BOO_enhance_value) {
				this.enhanceSelectMultiple(OBJ_config_entry, OBJ_option_data, ARR_values, STR_display_value);
			} // end if

		} // end for

		// Wenn der angezeigte Wert mit Daten angereichert wurde, setze ihn neu
		if (BOO_enhance_value) {
			if (!shouldSort) {
				// Die Sortierreihenfolge wie gespeichert verwenden
				ARR_values = OBJ_config_entry.value.map(id => mapById[id]).filter(Boolean);
			}
			OBJ_config_entry.value = ARR_values;
		} // end if

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'fillOptionsSelectMultiple'
			},
			"Das ist der Wert meiner Funktion",
			OBJ_config_entry.value
		);

	} // end function fillOptionsSelectMultiple

	/**
	 * Function > fillExternalOptions
	 *
	 * @desc Befuelle Optionen von Dropdowns, die extern nachgeladen wurden
	 * @param OBJ_field
	 * @param ARR_contents_helper
	 */
	public fillExternalOptions(OBJ_field: DetailConfigurationField, ARR_contents_helper: Array<ContentsHelper>) {

		if (DetailService.externalOptions(OBJ_field)) {

			// Befuelle Config mit nachgeladenen Daten
			for (let OBJ_helper of ARR_contents_helper) {

				// Feld hat Optionen, die nachgeladen wurden
				if (
					OBJ_helper.url == OBJ_field._links.options.href ||
					// Bei abhaengigen Feldern ist in href platzhalter
					OBJ_helper.url == OBJ_field._links.options.href_temp
				) {

					switch (OBJ_field.type) {

						// Dropdown
						case "select":
							this.fillOptionsSelect(OBJ_field, OBJ_helper);
							break;
						case "formbuilder":
							// simply fill options field with reloaded data
							OBJ_field.options = OBJ_helper.getListingData();
							break;

						case "select_multiple":
							this.fillOptionsSelectMultiple(OBJ_field, OBJ_helper, OBJ_field._links.options.name);
							break;

						case "select_multiple_check":
						case "dual_list":
							if (OBJ_field._links.options.searchFields == null) {
								// Hole einfach die Werte aus dem Listing und gebe sie als Options an
								OBJ_field.options = OBJ_helper.getListingData();
							} // end if
							break;

						// array
						case "array":
						case "publicationobjects":
							// Hole Aufbau des arrays
							let ARR_array_value = OBJ_helper.getValue(OBJ_field.name);
							// Erstelle Felder des Arrays
							this.createArrayFields(ARR_array_value, OBJ_field);
							break;

					} // end switch

				} // end if

			} // end for

		} // end if

	} // end function fillExternalOptions

	/**
	 * Function > createParentFieldObservers
	 *
	 * @desc Erstelle Observer, die alle abhaengigen Fedler ueber Aenderungen des Elternfeldes benachrichtigen
	 * @param ARR_dependencies
	 * @param ARR_form_fields
	 * @param BOO_hide_on_select
	 */
	createParentFieldObservers(ARR_dependencies, ARR_form_fields, BOO_hide_on_select = false) {

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'createParentFieldObservers'
			},
			"erschaffe Obersever fuer anhaengigkeiten",
			ARR_dependencies
		);

		// erstelle Observer, die melden, wenn sich Feld aendert
		for (let OBJ_dependency of ARR_dependencies) {

			let STR_parent_field = Object.keys(OBJ_dependency)[0];
			this.OBJ_subscription_service.createSubject(STR_parent_field);

		} // end for

	} // end createParentFieldObservers

	/**
	 * Function > iframe
	 * @param STR_link_to_frame
	 * @param STR_html_field
	 * @param STR_editortype
	 * @param BOO_htmlcontent
	 * @return {Observable<Object>}
	 */
	postToIFrame(STR_link_to_frame: string, STR_html_field = "", STR_editortype = 'default', BOO_htmlcontent = true) {
		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'postToIFrame'
			},
			STR_link_to_frame
		);

		return this.OBJ_http.post(
			STR_link_to_frame,
			// Daten aus Formular holen, anreichern und an iFrame schicken
			Object.assign(
				{
					"additional_data": {
						"access_token": this.OBJ_helper_service.getFromLocalstorage('access').access_token,
						"id_field": "tig-html-editable",
						// Gebe weiter, wie target fuer iFrame lautet
						"target": window.location.origin,
						// In welchem Feld wird zu bearbeitendes HTML uebergeben
						"htmlfield": STR_html_field,
						"type": STR_editortype,
						"htmlcontent": BOO_htmlcontent
					}
				},
				this.OBJ_detail_content.updateData(this.OBJ_detail_configuration.fields)
			),
			{responseType: 'text'}
		);

	} // end function iframe

	/**
	 * Function > getUpdatedData
	 *
	 * @desc Gib die durch Nutzereingaben aktualisierte Json-Daten zurueck
	 */
	public getUpdatedData() {
		return this.OBJ_detail_content.updateData(this.OBJ_detail_configuration.fields);
	} // end function getUpdatedData

	/**
	 * Function > getUpdatedData
	 *
	 * @desc Gib die durch Nutzereingaben aktualisierte Json-Daten zurueck
	 */
	public getSaveSeperately() {
		let ARR_save_seperately = [];
		this.OBJ_detail_content.updateData(this.OBJ_detail_configuration.fields, ARR_save_seperately);
		return ARR_save_seperately;
	} // end function getUpdatedData

	/**
	 * Function > getIFrame
	 * @param STR_link_to_frame
	 * @return {Observable<Object>}
	 */
	getIFrame(STR_link_to_frame: string) {
		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'getIFrame'
			},
			STR_link_to_frame
		);

		return this.OBJ_http.get(
			STR_link_to_frame,
			{responseType: 'text'}
		);

	} // end function getIFrame

	/**
	 * Function > getHelper
	 * @desc Holt Inhalte aus dem Backend ueber REST-API und gibt ihn verpackt in Helper-Klasse zurueck
	 * @param STR_url
	 * @param OBJ_params
	 * @param STR_origin - where does request come from? Only for debugging purposes
	 * @returns {Observable<ContentsHelper>}
	 */
	getHelper(STR_url: string, OBJ_params: HttpParams = null, STR_origin: string = ''): Observable<ContentsHelper> {
		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'getHelper'
			},
			'URL: ' + STR_url,
			'origin: ' + STR_origin
		);

		return this.OBJ_http.get(STR_url, {
			params: OBJ_params,
			observe: 'response'
		}).pipe(
			// for debugging purposes only
			map(OBJ_response => new ContentsHelper(OBJ_response.body, OBJ_response.headers.get("Content-Type"), STR_url))
		); // end pipe

	} // end function > getHelper

	/**
	 * Function > addGeneralFields
	 *
	 * @desc Fuege Felder hinzu, die jeder Datensatz hat
	 * @param ARR_fields
	 * @param OBJ_contents_helper
	 */
	private static addGeneralFields(ARR_fields: DetailConfigurationField[], OBJ_contents_helper: ContentsHelper) {

		// Trennlinie hinzufuegen
		ARR_fields.push({
			"name": "separation line",
			"type": "line",
		});

		let ARR_inner_fields = [];

		ARR_inner_fields.push({
			"name": "_creauser",
			"type": "input",
			"readonly": true,
			"_links": {
				"route": {
					"href": "users/users",
					"label": "oUser.sShowUser"
				}
			}
		});

		// Erstelldatum
		ARR_inner_fields.push({
			"name": "_creadate",
			"type": "date",
			"readonly": true
		});

		ARR_inner_fields.push({
			"name": "_modiuser",
			"type": "input",
			"readonly": true,
			"_links": {
				"route": {
					"href": "users/users",
					"label": "oUser.sShowUser"
				}
			}
		});

		// Aenderungsdatum
		ARR_inner_fields.push({
			"name": "_modidate",
			"type": "date",
			"readonly": true
		});

		// Fuege neue Felder zusammengefasst unter einem Objekt hinzu
		ARR_fields.push({
			"name": "systeminfo",
			"type": "systeminfo",
			// Die tatsaechlichen Daten sind nicht verschachtelt, sollen aber auf der Oberflaeche grafisch zusammengefasst werden
			"flat_object": true,
			"fields": ARR_inner_fields
		});

	} // end function addGeneralFields

	/**
	 * @desc Sind Optionen mitgegeben oder mueseen sie extern nachgeladen werden?
	 * @param {DetailConfigurationField} OBJ_field
	 * @return {boolean}
	 */
	public static externalOptions(OBJ_field: DetailConfigurationField) {

		if (OBJ_field._links != null && OBJ_field._links.options != null) {
			return true;
		} // end if

		return false;

	} // end function externalOptions

	/**
	 * @desc Ist Aufbau eines Elementes (idR array) extern nachzuladen
	 * @param {DetailConfigurationField} OBJ_field
	 * @return {boolean}
	 */
	public static externalComposition(OBJ_field: DetailConfigurationField) {

		if (OBJ_field._links != null && OBJ_field._links.composition != null) {
			return true;
		} // end if

		return false;

	} // end function externalOptions

	/**
	 * @desc Haengt Aufbau eines Elementes von weiteren Feldern ab?
	 * @param {DetailConfigurationField} OBJ_field
	 * @return {boolean}
	 */
	public static externalCompositionHasDependency(OBJ_field: DetailConfigurationField) {

		if (OBJ_field._links != null && OBJ_field._links.composition != null && OBJ_field._links.composition.depends_on != null) {
			return true;
		} // end if

		return false;

	} // end function externalCompositionHasDependency

	/**
	 * Function > enhanceLink
	 *
	 * @param OBJ_config_entry
	 * @param OBJ_link
	 * @param OBJ_contents_helper
	 * @param ARR_form_fields
	 * @param ARR_observables
	 * @return boolean
	 */
	enhanceLink(OBJ_config_entry, OBJ_link, OBJ_contents_helper, ARR_form_fields, ARR_observables): boolean {

		// Link, von dem Optionen nachgeladen werden
		let STR_url = OBJ_link.href;

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'enhanceLink'
			},
			"enhancing the link: " + STR_url
		);

		let BOO_url_complete = true;

		// falls Platzhalter im link verwendet wurden, woraus sollen diese befuellt werden?
		if (OBJ_link.depends_on) {

			for (const STR_key of Object.keys(OBJ_link.depends_on)) {

				let STR_value = null;

				// Werte wurden nachgeladen
				if (OBJ_contents_helper != null) {
					STR_value = OBJ_contents_helper.getValue(OBJ_link.depends_on[STR_key]);
				}
				// Werte sind bereits gesetzt
				else {

					let OBJ_entry = ARR_form_fields.find(OBJ_config_entry => OBJ_config_entry.name === OBJ_link.depends_on[STR_key]);
					if (typeof OBJ_entry === 'undefined') {
						OBJ_entry = ARR_form_fields.find(OBJ_config_entry => OBJ_config_entry.name === 'assignments');
						STR_value = OBJ_entry.fields.find(OBJ_config_entry => OBJ_config_entry.name === OBJ_link.depends_on[STR_key]).value;
					} else {
						STR_value = OBJ_entry.value;
					}

				} // end if

				if (STR_value) {
					// Link ergaenzen
					STR_url = STR_url.replace(STR_key, STR_value);
				}
				// In URL soll Parameter ersetzt werden, das soll auch bei leerem Wert geschehen
				else if (OBJ_link.depends_on_is_optional) {
					STR_url = STR_url.replace(STR_key, "");
				} else {
					// solange einer der Werte fehlt, von denen das Feld abhaengig ist, kann es nicht nachgealden werden
					OBJ_config_entry.visible = false;
					BOO_url_complete = false;
					break;
				} // end if

			} // end for

		} // end if

		// Die Daten koennen nur nachgeladen werden, wenn man schon weiss, woher sie stammen sollen
		if (BOO_url_complete && STR_url) {

			OBJ_config_entry.visible = true;

			// Speichere URL zwischen, um nachgeladene Inhalte matchen zu koennen
			OBJ_link.href_temp = STR_url;

			// Fuege URL zum Nachalden dazu
			if (this.ARR_urls_to_reload.indexOf(STR_url) == -1) {
				ARR_observables.push(this.getHelper(STR_url, null, 'enhanceLink'));
				this.ARR_urls_to_reload.push(STR_url);
			} // end if

		} // end if

		return BOO_url_complete;

	} // end function enhanceLink

	/**
	 * Function > enhanceSelectMultiple
	 *
	 * @desc erweitert die ausgewählten Werte, wenn nur die ID gespeichert wurde, so dass sie dennoch korrekt angezeigt werden
	 * @param OBJ_config_entry
	 * @param OBJ_option
	 * @param ARR_values
	 * @param $STR_display_field
	 */
	enhanceSelectMultiple(OBJ_config_entry, OBJ_option, ARR_values, $STR_display_field) {

		// kommt aktuell eingetragener Wert in den Optionen vor? Um Uneinheitlichkeiten bei Mandanten- und NetzwerkID zu begegngen kann teste bei ID auf string oder number.
		if (
			OBJ_config_entry.value.indexOf(String(OBJ_option.id)) !== -1 ||
			OBJ_config_entry.value.indexOf(Number(OBJ_option.id)) !== -1
		) {
			// Um Feld korrekt anzuzeigen setzte das Textfeld
			ARR_values.push({
				"id": OBJ_option.id,
				"name": $STR_display_field
			});
		} // end if

	} // end function enhanceSelectMultiple

	/**
	 * Function > enhanceField
	 *
	 * @param OBJ_config_entry
	 * @param ARR_observables
	 * @param OBJ_contents_helper ContentsHelper
	 * @param ARR_form_fields
	 * @param {string} STR_path_to_value
	 * @param readonly
	 */
	enhanceField(OBJ_config_entry: DetailConfigurationField, ARR_observables, OBJ_contents_helper, ARR_form_fields = [], STR_path_to_value = '', readonly: boolean = false) {

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'enhanceField'
			},
			"Enhancing field",
			OBJ_config_entry
		);

		// Je nach Feldart andere Vorgehensweise
		switch (OBJ_config_entry.type) {
			// Json wird uebergeben, wandle hier zum bearbeiten in string um
			case "textarea_json":
				let ARR_tmp = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				if (ARR_tmp) {
					OBJ_config_entry.value = JSON.stringify(ARR_tmp);
				} // end if
				break;

			// Array mit Aufbau der von Backend kommt
			case "array":
			case "publicationobjects":
				// Existiert Aufbau schon?
				let ARR_array_value: Publicationobject = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				// Array wurde schon befuellt, Felder muessen nur noch konfiguriert werden
				if (ARR_array_value) {
					// Speichere aktuellen Aufbau der Composition zwischen
					this.ARR_former_composition[OBJ_config_entry.name] = ARR_array_value;
				} // end if

				this.OBJ_helper_service.logWithClassAndFunction("DetailService", "enhanceField", "values set", OBJ_config_entry);

				// Aufbau des arrays kommt von extern
				if (OBJ_config_entry._links && OBJ_config_entry._links.composition) {

					this.enhanceLink(OBJ_config_entry, OBJ_config_entry._links.composition, OBJ_contents_helper, ARR_form_fields, ARR_observables);
					OBJ_config_entry.fields = [];

				} // end if

				// Muessen die Optionen für den array noch nachgeladen werden?
				if (OBJ_config_entry.fieldconfig != null) {

					// Sammle alle urls, die nachgeladen werden muessen
					for (let OBJ_inner_field of OBJ_config_entry.fieldconfig) {

						// Hat Dropdown Link mit hinterlegten Werten?
						if (DetailService.externalOptions(OBJ_inner_field)) {
							// Fuege URL zum Nachalden dazu
							if (this.ARR_urls_to_reload.indexOf(OBJ_inner_field._links.options.href) == -1) {
								ARR_observables.push(this.getHelper(OBJ_inner_field._links.options.href, null, 'enhanceField ' + 'Fieldname:' + OBJ_config_entry.name));
								this.ARR_urls_to_reload.push(OBJ_inner_field._links.options.href);
							} // end if
						} // end if

					} // end for

				} // end if
				break;

			// array dessen Aufbau schon in config uebergeben wird
			// assignments
			case "array_static":

				// get assigned values from backend
				let ARR_array_values = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				// loop over defined fields
				for (let OBJ_inner_field of OBJ_config_entry.fields) {

					// field has a link for options
					if (DetailService.externalOptions(OBJ_inner_field)) {

						// add url to load values
						if (this.ARR_urls_to_reload.indexOf(OBJ_inner_field._links.options.href) == -1) {

							ARR_observables.push(this.getHelper(OBJ_inner_field._links.options.href, null, 'enhanceField ' + 'Fieldname:' + OBJ_config_entry.name));
							this.ARR_urls_to_reload.push(OBJ_inner_field._links.options.href);

						} // end if

					} else if (OBJ_inner_field.type === 'object') {

						// loop over defined fields
						for (let OBJ_field of OBJ_inner_field.fields) {

							// add url to load values
							if (
								typeof OBJ_field._links !== 'undefined' &&
								this.ARR_urls_to_reload.indexOf(OBJ_field._links.options.href) == -1
							) {

								ARR_observables.push(this.getHelper(OBJ_field._links.options.href, null, 'enhanceField ' + 'Fieldname:' + OBJ_config_entry.name));
								this.ARR_urls_to_reload.push(OBJ_field._links.options.href);

							} // end if

						} // end for

					} // end if

					this.setValueToAssignedField(ARR_array_values, OBJ_inner_field);

				} // end for
				break;

			// array with assignments
			case "assignments":

				// get assigned values from backend
				this.ARR_values = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				// loop over assignments
				for (let OBJ_entry_field of OBJ_config_entry.fields) {

					// link enhanced
					let BOO_link_enhanced = false;

					// if entry has fields , then go through the fields
					if (typeof OBJ_entry_field.fields !== 'undefined') {

						// go through the defined fields
						for (let OBJ_entry_field_field of OBJ_entry_field.fields) {

							// if entry has fields
							if (
								typeof OBJ_config_entry.fields !== 'undefined' &&
								typeof OBJ_entry_field_field._links !== 'undefined' &&
								typeof OBJ_entry_field_field._links.options !== 'undefined'
							) {

								BOO_link_enhanced = this.enhanceLink(
									OBJ_entry_field_field, OBJ_entry_field_field._links.options, null, OBJ_config_entry.fields, ARR_observables
								);

							}

							// get object with values
							let OBJ_values = this.ARR_values.find(
								OBJ_value => OBJ_value.title === OBJ_entry_field.object_structure.title
							);

							// enhance assignment field
							this.enhanceAssignmentField(
								OBJ_entry_field_field, OBJ_values, OBJ_config_entry.fields, !BOO_link_enhanced ? ARR_observables : null
							);

						} // end for

					} else {

						// options can be loaded via url
						if (
							typeof OBJ_entry_field._links !== 'undefined' &&
							typeof OBJ_entry_field._links.options !== 'undefined'
						) {

							BOO_link_enhanced = this.enhanceLink(
								OBJ_entry_field, OBJ_entry_field._links.options, null, OBJ_config_entry.fields, ARR_observables
							);

						}

						// get object with values
						let OBJ_values = this.ARR_values.find(
							OBJ_value => OBJ_value.title === OBJ_entry_field.object_structure.title
						);

						// enhance assignment field
						this.enhanceAssignmentField(
							OBJ_entry_field, OBJ_values, OBJ_config_entry.fields, !BOO_link_enhanced ? ARR_observables : null
						);

					} // end if

					let ARR_enhanced_assignments = [];
					ARR_enhanced_assignments[OBJ_entry_field.name] = OBJ_entry_field;

				} // end for
				break;

			case "systeminfo":
			case "object":

				if (OBJ_config_entry.flat_object) {
					// Objekt dient nur der graphischen Zusammenfassung von Daten, im Json selbst sind die Daten nicht verschachtelt
					this.fillFormWithValues(OBJ_config_entry.fields, OBJ_contents_helper, ARR_observables, OBJ_config_entry.name);
				} else {
					this.fillFormWithValues(OBJ_config_entry.fields, OBJ_contents_helper, ARR_observables, STR_path_to_value + OBJ_config_entry.name, true, OBJ_config_entry.readonly);
				} // end if

				break;

			case "date":

				let STR_value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				if (STR_value) {
					OBJ_config_entry.value = new Date(Date.parse(STR_value));
				} // end if

				break;

			case "time":

				let STR_time = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				// Wenn Zeit noch in altem Format (UTC) vorliegt, passe es an
				if (STR_time) {
					// TODO: kann raus wenn Import durch ist - nur eingebaut um mit UTC-Zeit (Format 22:00:00Z) kompatibel zu bleiben
					if (STR_time.includes("Z")) {
						let ARR_time = STR_time.split(":");
						STR_time = ARR_time[0] + ":" + ARR_time[1];
					} // end if

					OBJ_config_entry.value = STR_time;
				} // end if

				break;

			// Dropdown
			case "select":

				// Hat Dropdown Link mit hinterlegten Werten?
				if (DetailService.externalOptions(OBJ_config_entry)) {

					// console log
					this.OBJ_helper_service.log
					(
						{
							class: 'DetailService',
							function: 'enhanceField'
						},
						"have select with external options",
						ARR_observables
					);

					this.enhanceLink(OBJ_config_entry, OBJ_config_entry._links.options, OBJ_contents_helper, ARR_form_fields, ARR_observables);

				} // end if

				OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				if (OBJ_config_entry.value == null) {
					OBJ_config_entry.value = "";
				} // end if

				// console log
				this.OBJ_helper_service.log
				(
					{
						class: 'DetailService',
						function: 'enhanceField'
					},
					"got following value for my select field: ",
					OBJ_config_entry.value
				);

				break;

			case "iframe":

				// generate url for iFrame
				let STR_url = OBJ_contents_helper.getValue(STR_path_to_value + "url"),
					STR_pattern = /^((http|https|ftp):\/\/)/;

				if (!STR_pattern.test(STR_url)) {
					STR_url = window.location.origin + STR_url;
				} // end if

				OBJ_config_entry.value = [STR_url];

				// console log
				this.OBJ_helper_service.log
				(
					{
						class: 'DetailService',
						function: 'enhanceField'
					},
					"OBJ_config_entry",
					OBJ_config_entry
				);

				break;

			case "file_upload":

				// Objekt selbst ist Bildobjekt
				if (OBJ_config_entry.file_object) {
					// Ist der Dateiname gesetzt (d.h. Datei ist bereits hochgeladen)? In diesem Fall zeige URL an
					if (this.OBJ_detail_content.getValue("filename")) {
						// Baue URL zusammen, unter der bild zu finden ist
						OBJ_config_entry.value =
							this.OBJ_detail_content.getValue("server") +
							this.OBJ_detail_content.getValue("folderprefix") +
							this.OBJ_detail_content.getValue("folder") +
							this.OBJ_detail_content.getValue("filename") +
							"." + this.OBJ_detail_content.getValue("ext");

					} // end if

				}
				// Bild haengt als Objekt innerhalb eines anderen Objektes
				else {

					let OBJ_image = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

					// Bild bereits hochgeladen
					if (OBJ_image != null) {

						// erstelle URL zum BIld
						if (OBJ_image.version != null) {
							OBJ_config_entry.value = OBJ_image.folderprefix + OBJ_image.folder + OBJ_image.filename + "_" + OBJ_image.version + "." + OBJ_image.ext;
						} else {
							OBJ_config_entry.value = DetailService.getImageHref(OBJ_image);
						} // end if

					} // end if

				} // end if

				break;

			case "checkbox":

				OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);
				break;

			case "select_multiple":

				// Hat Dropdown Link mit hinterlegten Werten?
				if (DetailService.externalOptions(OBJ_config_entry)) {
					this.enhanceLink(OBJ_config_entry, OBJ_config_entry._links.options, OBJ_contents_helper, ARR_form_fields, ARR_observables);
				} // end if

				OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);

				if (OBJ_config_entry.value == null) {
					// Wenn Wert leer ist und per config defualtvalue vorgegeben ist befuelle Feld damit
					if (OBJ_config_entry.defaultvalue) {
						OBJ_config_entry.value = OBJ_config_entry.defaultvalue;

					} else {
						OBJ_config_entry.value = [];
					} // end if

				}

				// Wenn Optionen mitgegeben sind und nur ID gespeichert wurde, passe hier die Darstellung des Feldes an
				if (OBJ_config_entry.id_only && !DetailService.externalOptions(OBJ_config_entry)) {

					let ARR_values = [];
					for (let OBJ_option of OBJ_config_entry.options) {
						// Passe Anzeige an
						this.enhanceSelectMultiple(OBJ_config_entry, OBJ_option, ARR_values, OBJ_option.name);
					} // end for

					// Setzte erweiterten Wert
					OBJ_config_entry.value = ARR_values;

				} // end if

				// console log
				this.OBJ_helper_service.log
				(
					{
						class: 'DetailService',
						function: 'enhanceField'
					},
					"got following value for my select_multiple field: ",
					OBJ_config_entry.value
				);

				break;

			case "select_multiple_check":

				// Hat Dropdown Link mit hinterlegten Werten?
				if (DetailService.externalOptions(OBJ_config_entry)) {
					this.enhanceLink(OBJ_config_entry, OBJ_config_entry._links.options, OBJ_contents_helper, ARR_form_fields, ARR_observables);
				} // end if

				OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);
				break;

			case "dual_list":

				// Link mit hinterlegten Werten?
				if (DetailService.externalOptions(OBJ_config_entry)) {

					// console log
					this.OBJ_helper_service.log
					(
						{
							class: 'DetailService',
							function: 'enhanceField'
						},
						"External options found, enhancing"
					);

					this.enhanceLink(OBJ_config_entry, OBJ_config_entry._links.options, OBJ_contents_helper, ARR_form_fields, ARR_observables);

				} // end if

				OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);
				break;

			case "hidden":
			default:

				// Soll Wert nachgeladen werden und nicht direkt gesetzt? benoetigt bei crea- bzw- modiusern,aktuell nicht verwendet
				if (OBJ_config_entry._links && OBJ_config_entry._links.value) {

					// console log
					this.OBJ_helper_service.log
					(
						{
							class: 'DetailService',
							function: 'enhanceField'
						},
						"Loading value"
					);

					// Fuege URL zum Nachalden dazu
					if (this.ARR_urls_to_reload.indexOf(OBJ_config_entry._links.value.href) == -1) {
						ARR_observables.push(this.getHelper(OBJ_config_entry._links.value.href, null, 'enhanceField ' + 'Fieldname:' + OBJ_config_entry.name));
						this.ARR_urls_to_reload.push(OBJ_config_entry._links.value.href);
					} // end if

				} else {
					OBJ_config_entry.value = OBJ_contents_helper.getValue(STR_path_to_value + OBJ_config_entry.name);
				} // end if

		} // end switch

		// Wenn Wert leer ist und per config defualtvalue vorgegeben ist befuelle Feld damit
		if (OBJ_config_entry.defaultvalue && !OBJ_config_entry.value) {
			OBJ_config_entry.value = OBJ_config_entry.defaultvalue;
		} // end if

		// Feld ist nur einmalig bearbeitbar
		if (OBJ_config_entry.immutable_when_filled) {

			if (OBJ_config_entry.value != null && (OBJ_config_entry.value.length === undefined || OBJ_config_entry.value.length > 0)) {
				OBJ_config_entry.readonly = true;
			} // end if

		} // end if

		// REadonly-Status des Feldes ist von Inhalt eines anderen Feldes abhaengig
		if (OBJ_config_entry.readonlyfield && OBJ_contents_helper.getValue(OBJ_config_entry.readonlyfield)) {
			OBJ_config_entry.readonly = OBJ_contents_helper.getValue(OBJ_config_entry.readonlyfield);
		}

		// Feld befindet sich innerhalb eines Objektes, das nicht bearbeitet werden soll
		if (readonly) {
			OBJ_config_entry.readonly = true;
		}

	} // end function enhanceField

	/**
	 * @param OBJ_field
	 * @param OBJ_values
	 * @param ARR_fields
	 * @param ARR_observables
	 */
	private enhanceAssignmentField(OBJ_field, OBJ_values, ARR_fields, ARR_observables): void {

		if (
			ARR_observables !== null &&
			// field have a link for the options
			DetailService.externalOptions(OBJ_field) &&
			// add url to load the options
			this.ARR_urls_to_reload.indexOf(OBJ_field._links.options.href) == -1
		) {

			this.enhanceLink(OBJ_field, OBJ_field._links, null, ARR_fields, ARR_observables);
			// ARR_observables.push(this.getHelper(OBJ_field._links.options.href));
			this.ARR_urls_to_reload.push(OBJ_field._links.options.href);

		} // end if

		OBJ_field.value = OBJ_field.type === 'select' ? '' : null;
		if (typeof OBJ_values !== 'undefined') {

			// correct inner field found
			switch (OBJ_field.type) {

				// drop down
				case 'select':
					OBJ_field.value = OBJ_values.ids;
					if (typeof OBJ_field.object_structure !== 'undefined') {
						OBJ_field.object_structure.ids = OBJ_values.ids;
					} // end if
					break;

				// input field
				case 'input':
					switch (OBJ_field.name) {

						case 'ids':
							OBJ_field.value = OBJ_values.ids;
							if (typeof OBJ_field.object_structure !== 'undefined') {
								OBJ_field.object_structure.ids = OBJ_values.ids;
							} // end if
							break;

						case 'value':
							OBJ_field.value = OBJ_values.value;
							if (typeof OBJ_field.object_structure !== 'undefined') {
								OBJ_field.object_structure.value = OBJ_values.value;
							} // end if
							break;

					} // end switch
					break;

			} // end switch

		} // end if

	} // end function enhanceAssignmentField

	/**
	 * @param ARR_array_values
	 * @param OBJ_inner_field
	 */
	private setValueToAssignedField(ARR_array_values, OBJ_inner_field) {

		// get object with value
		let OBJ_value = ARR_array_values;
		if (Array.isArray(ARR_array_values)) {
			// get object with value
			OBJ_value = ARR_array_values.find(OBJ_value => OBJ_value.title === OBJ_inner_field.object_structure.title);
		}

		// correct inner field found
		switch (OBJ_inner_field.type) {

			case 'select':
				OBJ_inner_field.value = "";
				// value/s available
				if (
					typeof OBJ_value !== 'undefined' &&
					Array.isArray(OBJ_value.ids) &&
					OBJ_value.ids.length > 0
				) {
					// Name wird spaeter aus den optionen befuellt, hier ist nur ID bekannt
					OBJ_inner_field.value = OBJ_value.ids;
				} // end if
				break;
			case 'select_multiple':
			case 'select_multiple_check':
				OBJ_inner_field.value = [];
				// value/s available
				if (
					typeof OBJ_value !== 'undefined' &&
					Array.isArray(OBJ_value.ids) &&
					OBJ_value.ids.length > 0
				) {
					// Name wird spaeter aus den optionen befuellt, hier ist nur ID bekannt
					OBJ_inner_field.value = OBJ_value.ids;
				} // end if
				break;

			case 'file_upload':
				if (
					typeof OBJ_value !== 'undefined' &&
					// aktuell heisst Feld unter dem nachgeladene assignments haengen object
					OBJ_value.loaded_assignments != null &&
					OBJ_value.loaded_assignments.length > 0
				) {
					// akutell gibt es keine loesung fuer mehrere Fileuploads pro assignment, hole erste Datei
					OBJ_inner_field.value = DetailService.getImageHref(OBJ_value.loaded_assignments[0]);
					// Setze ids, um sie wieder nach hinten speichern zu koennen, falls sich an Feld nichts aendert
					OBJ_inner_field.object_structure.ids = OBJ_value.ids;
				} // end if
				break;

			case 'object':
				if (
					typeof OBJ_value !== 'undefined' &&
					Array.isArray(OBJ_value.ids) &&
					OBJ_value.ids.length > 0
				) {

					// loop over defined fields
					for (let OBJ_field of OBJ_inner_field.fields) {

						switch (OBJ_field.name) {

							case 'ids':
								OBJ_field.value = OBJ_value.ids;
								break;

							case 'value':
								OBJ_field.value = OBJ_value.value;
								break;

						} // end switch

					} // end for

					// set ids
					OBJ_inner_field.object_structure.ids = OBJ_value.ids;

					// set value
					OBJ_inner_field.object_structure.value = OBJ_value.value;

				} // end if
				break;

		} // end switch

	} // end function setValueToAssignedField

	/**
	 * Function > post
	 * @param OBJ_image
	 */
	static getImageHref(OBJ_image) {
		return OBJ_image.server + OBJ_image.folderprefix + OBJ_image.folder + OBJ_image.filename + "." + OBJ_image.ext;
	} // end function post

	/**
	 * Function > create
	 * @param OBJ_detail_config
	 * @param OBJ_params
	 * @return {Observable<Object>}
	 */
	create(OBJ_detail_config, OBJ_params: HttpParams = null) {
		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'create'
			},
			"creating stuff",
			OBJ_detail_config.fields,
			OBJ_params
		);

		return this.upload(OBJ_detail_config, true, OBJ_params);

	} // end function create

	/**
	 * Function > create
	 * @param OBJ_detail_config
	 * @param OBJ_params
	 * @return {Observable<Object>}
	 */
	bulkInsert(OBJ_detail_config, OBJ_params: HttpParams = null) {
		let OBJ_updated_data = this.OBJ_detail_content.updateData(OBJ_detail_config.fields);
		let ARR_observables = [];
		let ARR_keywords = [];
		let STR_fieldname: string;

		// Suche Bulkfield und splitte Eintraege anhand uebergebenem Seperator auf
		OBJ_detail_config.fields.forEach(field => {
			if (field.type === "bulkinput") {
				ARR_keywords = field.value.split(field.seperator);
				STR_fieldname = field.name;
			}
		})
		// Anfragen URL - da es sich um ein create handelt, wird allegemeine URL verwendet
		let STR_request_url = DetailService.getLinkToListing(this.OBJ_detail_content.getLinkToSelf())
		// fuer jeden eintrag einen call ans Backend schicken, Inhalt: Json  in dem nur das keyowrd feld ausgetauscht ist
		ARR_keywords.forEach(
			value => {
				let OBJ_data = Object.assign({}, OBJ_updated_data);
				OBJ_data[STR_fieldname] = value.trim();
				// Pro keyword eine Anfrage erstellen
				ARR_observables.push(this.OBJ_http.post(STR_request_url, OBJ_data).pipe(
					// Errorhandling verbauen, damit forkjoin nicht beim ersten Fehler komplett abbricht
					catchError(err =>
						of(err.status)
					)));
			}
		)

		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'create'
			},
			"creating stuff",
			OBJ_detail_config.fields,
			OBJ_params
		);

		// Schicke alle Requests parallel ans Backend
		return forkJoin(ARR_observables).pipe(
			map(ARR_responses => {

				let OBJ_return = {
					"error": [],
					"duplicate": [],
					"successful": []
				};

				ARR_responses.forEach(
					(OBJ_response, INT_index) => {
						// unterscheide nochmal nach Fehlertyp
						if (typeof OBJ_response === "number") {
							if (OBJ_response === 409) {
								OBJ_return.duplicate.push(ARR_keywords[INT_index])
							} else {
								OBJ_return.error.push(ARR_keywords[INT_index])
							} // end if
						} else {
							OBJ_return.successful.push(ARR_keywords[INT_index])
						} // end if

					} // end foreach
				)

				return OBJ_return;
			})
		);

	} // end function bulkInsert

	/**
	 * Function > update
	 *
	 * @param OBJ_detail_config
	 * @param OBJ_params
	 * @return {Observable<Object>}
	 */
	update(OBJ_detail_config, OBJ_params: HttpParams = null) {
		// console log
		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'update'
			},
			"Updating",
			OBJ_detail_config.fields
		);

		return this.upload(OBJ_detail_config, false, OBJ_params);

	} // end function update


	/**
	 * Function > upload
	 * @return {Observable<Object>}
	 */
	upload(OBJ_detail_config, BOO_create: boolean, OBJ_params: HttpParams = null) {

		let ARR_save_seperately = [];
		let	OBJ_updated_data = this.OBJ_detail_content.updateData(OBJ_detail_config.fields, ARR_save_seperately);
		let	BOO_is_user_url = this.OBJ_router.url.includes('user');

		this.OBJ_helper_service.log
		(
			{
				class: 'DetailService',
				function: 'update'
			},
			"Updated data: ",
			OBJ_updated_data
		);

		let ARR_observables = [];

		// Verknuepfte Bilder oder Dateien werden an eigene Api-Schnittstelle geschickt und dann ihre ID am Datensatz gespeichert
		for (let OBJ_file_object of ARR_save_seperately) {

			// File oder image?
			let STR_request_url = "/api/images";

			if (typeof OBJ_file_object.self !== 'undefined' && OBJ_file_object.self)
			{
				STR_request_url = this.OBJ_detail_content.getLinkToSelf();
			}
			else if (OBJ_file_object.value)
			{
				if (!OBJ_file_object.value.type.startsWith('image'))
				{
					STR_request_url = "/api/files";
				} // end if
			}

			if (BOO_is_user_url && OBJ_file_object.object_structure.ids.length > 0) {
				// Seperater Request, da default ein 204 - No Content zurückkommt
				this.deleteFile(STR_request_url, OBJ_file_object.object_structure.ids[0]).subscribe((OBJ_data) => {
				}, error => {
					Raven.captureException(error);
				});
			} // end if

			if (
				typeof OBJ_file_object.object_structure !== 'undefined' &&
				OBJ_file_object.object_structure.delete
			) {
				// Hier koennte loeschen angestossen werden, wenn man bisher assignten Inhalt bei Rausnahme loeschen will
				continue;
			} // end if

			let OBJ_form_data = new FormData();
			OBJ_form_data.append('upload', OBJ_file_object.value);
			OBJ_file_object.value = null;
			OBJ_form_data.append('json', JSON.stringify(OBJ_file_object));

			let OBJ_http_params = new HttpParams();

			// Netzwerk mitgeben, in das Datei tatsaechlich gespeichert werden soll
			if (OBJ_file_object.hasOwnProperty('override_network')) {
				OBJ_http_params = OBJ_http_params.set('override_network', OBJ_file_object.override_network);
			} // end if

			// Wenn true dann wird nur netzwerkID an Datei gespeichert, nicht applicationID
			if (OBJ_file_object.hasOwnProperty('application_empty')) {
				OBJ_http_params = OBJ_http_params.set('application_empty', OBJ_file_object.application_empty);
			} // end if

			// Wenn true dann wird nur netzwerkID an Datei gespeichert, nicht applicationID
			if (OBJ_file_object.hasOwnProperty('categories')) {
				OBJ_http_params = OBJ_http_params.set('categories', OBJ_file_object.categories);
			} // end if

			ARR_observables.push(
				this.post(STR_request_url, OBJ_form_data, OBJ_http_params)
			);

		} // end for

		// Keine zusätzlichen Bilder oder Dateien hochzuladen und zu verknüpfen
		if (ARR_observables.length === 0) {
			return this.handleSimpleUpsert(BOO_create, OBJ_detail_config, OBJ_updated_data, OBJ_params);
		} // end if

		// Schicke alle Requests parallel ans Backend
		return forkJoin(ARR_observables).pipe(
			mergeMap(
				MIX_responses => {
					// Hole assignments und fuege neu angelegte Verknuepfung hinzu oder ergaenze sie, wenn sie bereits existiert
					let ARR_assignments = this.OBJ_detail_content.getValue("assignments");

					// Wenn es Fehler beim Speichern der Bilder gab, gebe Fehler weiter
					if (MIX_responses instanceof HttpErrorResponse)
					{
						return MIX_responses;
					}
					else
					{
						for (let INT_key = 0; INT_key < MIX_responses.length; INT_key++)
						{

							// get data of saved file from response
							let OBJ_saved_file = MIX_responses[INT_key];

							// Hole das entsprechende Feld Feld
							let OBJ_field: DetailConfigurationField = ARR_save_seperately[INT_key];

							// Hole im Feld hinterlegter Aufbau des Objektes
							let OBJ_structure = OBJ_field.object_structure;

							// Fuege IDs in vorgegebene Struktur ein
							OBJ_structure.ids = [OBJ_saved_file["id"]];
							if (ARR_assignments && ARR_assignments.length > 0)
							{

								let INT_index_existing_assignment = ARR_assignments.findIndex(OBJ_assignment => OBJ_assignment.title === OBJ_structure.title);

								if (INT_index_existing_assignment !== -1) {
									// overwrite existing assignment
									ARR_assignments[INT_index_existing_assignment] = OBJ_structure;
								}
								else
								{
									// add new assignment
									ARR_assignments.push(OBJ_structure);
								} // end if

							}
							else
							{
								// create array assignments & add new entry
								ARR_assignments.push(OBJ_structure);
							} // end if

						} // end for

						if (BOO_create) {
							return this.post(
								DetailService.getLinkToListing(this.OBJ_detail_content.getLinkToSelf()),
								OBJ_updated_data,
								OBJ_params
							);
						} else {
							return this.put(
								this.OBJ_detail_content.getLinkToSelf(),
								OBJ_updated_data,
								OBJ_params
							);

						} // end if

					}
				}
			));

	} // end function upload

	/**
	 * Function > delete
	 * @return {Observable<Object>}
	 */
	delete(OBJ_params: HttpParams = null) {
		let OBJ_options = OBJ_params ? {params: OBJ_params} : {};
		return this.OBJ_http.delete(
			this.OBJ_detail_content.getLinkToSelf(), OBJ_options);

	} // end function delete

	deleteFile(STR_url: string, STR_imageId: string) {
		return this.OBJ_http.delete(STR_url + '/' + STR_imageId);
	}

	/**
	 * Function > post
	 *
	 * @desc schicke POST-Request an Api (Elemente neu anlegen)
	 * @param {string} STR_url
	 * @param {FormData} OBJ_form_data
	 * @param OBJ_params
	 */
	post(STR_url: string, OBJ_form_data: FormData, OBJ_params: HttpParams = null)
	{
		let OBJ_options = OBJ_params ? {params: OBJ_params} : {};

		return this.OBJ_http.post(
			STR_url,
			OBJ_form_data,
			OBJ_options
		);
	} // end function post

	/**
	 * Function > get
	 *
	 * @desc schicke GET-Request an Api
	 * @param {string} STR_url
	 * @param OBJ_options
	 */
	get(STR_url: string, OBJ_options: object) {
		return this.OBJ_http.get(STR_url, OBJ_options);
	} // end function get

	/**
	 * Function > put
	 *
	 * @desc schicke PUT-Request an Api (Element updaten)
	 * @param {string} STR_url
	 * @param {FormData} OBJ_form_data
	 * @param OBJ_params
	 */
	put(STR_url: string, OBJ_form_data: FormData, OBJ_params: HttpParams = null) {
		let OBJ_options = OBJ_params ? {params: OBJ_params} : {};
		return this.OBJ_http.put(STR_url, OBJ_form_data, OBJ_options);
	} // end function post

	get STR_detailurl(): string {
		return this._STR_detailurl;
	}

	set STR_detailurl(value: string) {
		this._STR_detailurl = value;
	}

	get classname(): string {
		return this._classname;
	}

	set classname(value: string) {
		this._classname = value;
	}

	/**
	 * Function > getLinkToListing
	 * @desc if a new resource is created post to the listing api because we don't have an id yet
	 * @param STR_url
	 */
	static getLinkToListing(STR_url) {
		let ARR_parts = STR_url.split('/');

		ARR_parts.pop();

		return ARR_parts.join("/");
	} // end function getLinkToListing

	public getReloadedData() {
		return this.ARR_contents_helper;
	}

	/**
	 * Function > handleSimpleUpsert
	 *
	 * @param BOO_create
	 * @param OBJ_detail_config
	 * @param OBJ_updated_data
	 * @param OBJ_params
	 */
	private handleSimpleUpsert(BOO_create, OBJ_detail_config, OBJ_updated_data, OBJ_params: HttpParams = null) {
		// Neuen Datensatz anlegen - POST-Request abeschicken
		if (BOO_create) {

			// es handelt sich um Datei/Bilderupload - schicke Multipartformdata
			if (OBJ_detail_config.file_upload) {
				let OBJ_form_data = new FormData();
				OBJ_form_data.append('json', JSON.stringify(OBJ_updated_data));
				OBJ_form_data.append('upload', OBJ_detail_config.fields.find(MIX_file => MIX_file.name == 'upload').value, OBJ_detail_config.fields.find(MIX_file => MIX_file.name == 'title').value);
				return this.post(DetailService.getLinkToListing(this.OBJ_detail_content.getLinkToSelf()), OBJ_form_data, OBJ_params);
			} // end if

			return this.post(
				DetailService.getLinkToListing(this.OBJ_detail_content.getLinkToSelf()),
				OBJ_updated_data,
				OBJ_params
			);
		} // end if

		// Ansonsten wird ein upload durchgefuehrt
		// es handelt sich um Datei/Bilderupload - schicke Multipartformdata
		if (OBJ_detail_config.file_upload) {
			let OBJ_form_data = new FormData();
			OBJ_form_data.append('json', JSON.stringify(OBJ_updated_data));

			if (OBJ_detail_config.fields.find(MIX_file => MIX_file.name == 'upload').value instanceof Blob) {
				OBJ_form_data.append(
					'upload',
					OBJ_detail_config.fields.find(MIX_file => MIX_file.name == 'upload').value,
					OBJ_detail_config.fields.find(MIX_file => MIX_file.name == 'title').value
				);

			} // end if

			return this.put(this.OBJ_detail_content.getLinkToSelf(), OBJ_form_data, OBJ_params);
		} // end if

		// Update den Datensatz
		return this.put(
			this.OBJ_detail_content.getLinkToSelf(),
			OBJ_updated_data,
			OBJ_params
		);
	} // end function handleSimpleUpsert

} // end class DetailService
